import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions, UseFormPayload as UseFormBasePayload } from '@domain-group/react-hooks'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as portalActions from '@redux/modules/portal'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/types'

import defaultFormState, { requiredFields } from '@models/portal'
import type { PortalModel } from '@models/types'

const stringifyObjectKeys: (keyof PortalModel)[] = ['external_ftp', 'internal_ftp', 'options']

export const generateUrls = (portal: Partial<PortalModel> = {}) => {
  const { id } = portal

  return {
    editPortalRelationsUrl: `#/systemManager/portals/${id}/edit/relations`,
    editPortalUrl: `#/systemManager/portals/${id}/edit`,
    portalsIndexUrl: '#/systemManager/portals',
  }
}

type CreatePortalPayload = {
  dispatch: AppDispatch,
  portalParams: Partial<PortalModel>,
  requestOptions?: EntityOptions,
}

const createPortal = (params: CreatePortalPayload) => {
  const { dispatch, portalParams, requestOptions } = params
  const { createPortal: createFn } = portalActions

  const updatedParams = mergeAndStringifyEntityObjects({}, portalParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editPortalUrl } = generateUrls(data)

      return {
        data,
        redirectUrl: editPortalUrl,
        success,
      }
    }

    return response
  })
}

type UpdatePortalPayload = {
  dispatch: AppDispatch,
  portal: PortalModel,
  portalParams: PortalModel,
  requestOptions?: EntityOptions,
}

const updatePortal = (params: UpdatePortalPayload) => {
  const {
    dispatch, portal, portalParams, requestOptions,
  } = params
  const { updatePortal: updateFn } = portalActions

  const updatedParams = mergeAndStringifyEntityObjects(portal, portalParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function usePortalForm(
  portal: Partial<PortalModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
): UseFormBasePayload {
  const { customRequiredFields = [], validateOn } = options

  const portalForm = useForm(
    defaultFormState,
    { entity: portal, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [portal.id, portal.cache_key],
  )

  return portalForm
}

function usePortal(initEntity: Partial<PortalModel> = {}) {
  // @ts-expect-error TS2345
  const { entity: portal } = useLatestEntity(initEntity, 'portals')

  const dispatch = useDispatch()

  const {
    creating, loading, updating,
  } = useSelector(reduxState => reduxState.portals)

  return {
    callbacks: {
      createPortal: (portalParams: Partial<PortalModel>, requestOptions?: EntityOptions) => (
        createPortal({ portalParams, dispatch, requestOptions })
      ),
      updatePortal: (portalParams: PortalModel, requestOptions?: EntityOptions) => (
        updatePortal({
          // @ts-expect-error TS2322
          portal, portalParams, dispatch, requestOptions,
        })
      ),
    },
    creating,
    loading,
    portal,
    updating,
    // @ts-expect-error TS2345
    urls: generateUrls(portal),
  }
}

export type UsePortalPayload = ReturnType<typeof usePortal>

export default usePortal
